import React, {useCallback, useEffect, useMemo, useState} from "react";
import moment from "moment";
import {executeGet} from "../../Api";
import {amountFormatterWithZero} from "../../utils";
import {ViewStatement} from "../../Refactor/Statement/View/view-statement";
import "./payersTable.css";

// How the payer was told about a statement (new-sosoft `notifiedBy` values).
const NOTIFIED_LABELS = {
    emailed: "Emailed",
    texted: "Texted",
    mail: "Mail (no email or cell on file)",
};

const money = (value) => (value === null || value === undefined || value === "" ? "" : amountFormatterWithZero(parseFloat(value) || 0));

const payerName = (row) => {
    if (row.payer && (row.payer.payerFirst || row.payer.payerLast)) {
        return `${row.payer.payerFirst || ""} ${row.payer.payerLast || ""}`.trim();
    }
    return row.responsibleParty || "";
};

// Admin dashboard tab: every statement SOS has sent for this practice through the statements XML
// feed, newest first, with a read-only view of any one of them (same rendering the payer sees).
const AdminStatements = ({subdomain}) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [selectedLoading, setSelectedLoading] = useState(false);

    const fetchStatements = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await executeGet(`statements/admin?includeArchived=${includeArchived ? "true" : "false"}&limit=500`);
            setRows(result.data?.statements || []);
            setTotal(result.data?.total || 0);
        } catch (err) {
            console.error("Error fetching statements:", err);
            setError(err.response?.data?.error || err.message || "Error fetching statements");
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => {
        fetchStatements();
    }, [fetchStatements]);

    const openStatement = async (row) => {
        setSelectedLoading(true);
        setError("");
        try {
            const result = await executeGet(`statements/admin/${row._id}`);
            setSelected(result.data);
        } catch (err) {
            console.error("Error fetching statement:", err);
            setError(err.response?.data?.error || err.message || "Error fetching statement");
        } finally {
            setSelectedLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((row) => [
            payerName(row), row.patientName, row.patientAcctID, row.payerId, row.statementId, row.payer && row.payer.payerEmail,
        ].some((value) => value && String(value).toLowerCase().includes(term)));
    }, [rows, search]);

    if (selected) {
        return (
            <div className="intake-content-admin">
                <div className="d-flex justify-content-between align-items-center mb-2 px-3">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setSelected(null)}>
                        &larr; Back to statements
                    </button>
                    <div className="text-muted small">
                        Payer #{selected.payerId} &middot; Statement #{selected.statementId}
                        {selected.isArchived ? " · archived (a newer statement exists for this payer)" : ""}
                    </div>
                </div>
                <ViewStatement statement={selected}/>
            </div>
        );
    }

    return (
        <div className="intake-content-admin">
            <div className="jumbotron p-4 p-lg-5">
                <h2 className="mb-2">Statements</h2>
                <p className="text-muted mb-4">
                    Statements your billing system has sent to the portal for {subdomain}. Payers see their current statement
                    under the Statement tab when they log in. Archived statements have been replaced by a newer one for the same payer.
                </p>

                <div className="d-flex flex-wrap align-items-center mb-3" style={{gap: "12px"}}>
                    <input
                        type="text"
                        className="form-control"
                        style={{maxWidth: "320px"}}
                        placeholder="Search payer, patient, account # or payer #"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <label className="d-flex align-items-center mb-0" style={{gap: "6px"}}>
                        <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}/>
                        Show archived
                    </label>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={fetchStatements} disabled={loading}>
                        Refresh
                    </button>
                    <span className="text-muted small ms-auto">
                        {loading ? "Loading..." : `${filtered.length} of ${total} statement${total === 1 ? "" : "s"}`}
                    </span>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {!loading && !error && rows.length === 0 && (
                    <div className="text-muted">
                        No statements have been received for this practice yet. Statements arrive through the
                        SOSPORTALST.zip statements file uploaded by your billing system.
                    </div>
                )}

                {rows.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead>
                            <tr>
                                <th>Statement Date</th>
                                <th>Payer</th>
                                <th>Patient / Account</th>
                                <th className="text-end">Pay This Amount</th>
                                <th className="text-end">New Balance</th>
                                <th>Notified</th>
                                <th>Received</th>
                                <th>Status</th>
                                <th/>
                            </tr>
                            </thead>
                            <tbody>
                            {filtered.map((row) => (
                                <tr key={row._id}>
                                    <td>{row.statementDateText || (row.statementDate ? moment(row.statementDate).format("MM/DD/YYYY") : "")}</td>
                                    <td>
                                        <div>{payerName(row)}</div>
                                        <div className="text-muted small">
                                            Payer #{row.payerId}{row.payer ? "" : " · no portal account"}
                                        </div>
                                    </td>
                                    <td>
                                        <div>{row.patientName}</div>
                                        <div className="text-muted small">{row.patientAcctID}</div>
                                    </td>
                                    <td className="text-end">{money(row.pleasePay)}</td>
                                    <td className="text-end">{money(row.newBalance)}</td>
                                    <td>
                                        {(row.notifiedBy || []).map((how) => NOTIFIED_LABELS[how] || how).join(", ")}
                                        {row.mailRequestedAt && (
                                            <div className="text-muted small">
                                                {row.mailedAt ? `Mailed ${moment(row.mailedAt).format("MM/DD/YYYY")}` : "Print & mail requested"}
                                            </div>
                                        )}
                                    </td>
                                    <td>{row.receivedAt ? moment(row.receivedAt).format("MM/DD/YYYY h:mm A") : ""}</td>
                                    <td>{row.isArchived ? <span className="text-muted">Archived</span> : "Current"}</td>
                                    <td className="text-end">
                                        <button type="button" className="btn btn-sm sos-custom-btn-css text-white"
                                                disabled={selectedLoading} onClick={() => openStatement(row)}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminStatements;
